import { Fragment, useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import * as wh from '../../services/webhookIngestLogsApi'
import { Button } from '../../components/ui/Button'
import { Card, CardHeader } from '../../components/ui/Card'
import { Input } from '../../components/ui/Input'
import { SkeletonFormSections } from '../../components/ui/Skeleton'

const DEFAULT_LIMIT = 25

function formatWhen(iso: string) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  } catch {
    return iso
  }
}

function errMessage(e: unknown) {
  return e instanceof Error ? e.message : 'Something went wrong'
}

export default function StoreWebhookLogsPage() {
  const { id: orgRouteId, storeId: storeRouteId } = useParams()
  const organisationId = orgRouteId != null ? Number(orgRouteId) : NaN
  const storeId = storeRouteId != null ? Number(storeRouteId) : NaN

  const [items, setItems] = useState<wh.WebhookIngestLogRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [limit] = useState(DEFAULT_LIMIT)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [provider, setProvider] = useState<'' | 'mollie' | 'paystack'>('')
  const [orderPaymentMethod, setOrderPaymentMethod] = useState<'' | 'online' | 'pay_on_delivery'>('')
  const [outcome, setOutcome] = useState('')
  const [applied, setApplied] = useState({
    fromDate: '',
    toDate: '',
    provider: '' as '' | 'mollie' | 'paystack',
    orderPaymentMethod: '' as '' | 'online' | 'pay_on_delivery',
    outcome: '',
  })

  const load = useCallback(async () => {
    if (!Number.isFinite(organisationId) || !Number.isFinite(storeId)) return
    setLoading(true)
    setError(null)
    try {
      const a = applied
      const fromIso =
        a.fromDate.trim() !== '' ? new Date(`${a.fromDate.trim()}T00:00:00`).toISOString() : undefined
      const toIso =
        a.toDate.trim() !== '' ? new Date(`${a.toDate.trim()}T23:59:59.999`).toISOString() : undefined
      const data = await wh.fetchWebhookIngestLogsForOrg(organisationId, storeId, {
        page,
        limit,
        from: fromIso,
        to: toIso,
        provider: a.provider || undefined,
        orderPaymentMethod: a.orderPaymentMethod || undefined,
        outcome: a.outcome.trim() || undefined,
      })
      setItems(data.items)
      setTotal(data.total)
    } catch (e) {
      setError(errMessage(e))
    } finally {
      setLoading(false)
    }
  }, [organisationId, storeId, page, limit, applied])

  useEffect(() => {
    void load()
  }, [load])

  function applyFilters() {
    setApplied({
      fromDate,
      toDate,
      provider,
      orderPaymentMethod,
      outcome,
    })
    setPage(1)
  }

  const totalPages = Math.max(1, Math.ceil(total / limit) || 1)
  const invalid = !Number.isFinite(organisationId) || !Number.isFinite(storeId)

  return (
    <Card>
      <CardHeader
        title="Payment webhook logs"
        description="Mollie and Paystack callback audit for this store (duplicates, signature errors, payment outcomes). Platform super-admin only."
      />
      <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
        <Link
          to={`/organizations/${orgRouteId}/stores/${storeRouteId}`}
          className="text-primary underline-offset-4 hover:underline"
        >
          ← Back to store
        </Link>
      </div>

      {invalid ? (
        <p className="text-sm text-destructive">Invalid organisation or store in URL.</p>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-sm text-muted-foreground">
              From
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value)
                  setPage(1)
                }}
                className="w-[11rem]"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-muted-foreground">
              To
              <Input
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value)
                  setPage(1)
                }}
                className="w-[11rem]"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-muted-foreground">
              Gateway
              <select
                className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                value={provider}
                onChange={(e) => {
                  setProvider(e.target.value as '' | 'mollie' | 'paystack')
                  setPage(1)
                }}
              >
                <option value="">All</option>
                <option value="mollie">Mollie</option>
                <option value="paystack">Paystack</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm text-muted-foreground">
              Order payment
              <select
                className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                value={orderPaymentMethod}
                onChange={(e) => {
                  setOrderPaymentMethod(e.target.value as '' | 'online' | 'pay_on_delivery')
                  setPage(1)
                }}
              >
                <option value="">All</option>
                <option value="online">online</option>
                <option value="pay_on_delivery">pay_on_delivery</option>
              </select>
            </label>
            <label className="flex min-w-[8rem] flex-col gap-1 text-sm text-muted-foreground">
              Outcome
              <Input
                placeholder="e.g. payment_applied"
                value={outcome}
                onChange={(e) => {
                  setOutcome(e.target.value)
                  setPage(1)
                }}
              />
            </label>
            <Button type="button" variant="secondary" size="sm" onClick={() => applyFilters()} disabled={loading}>
              Apply
            </Button>
          </div>

          {error ? <p className="mb-4 text-sm text-destructive">{error}</p> : null}

          {loading ? (
            <SkeletonFormSections />
          ) : (
            <>
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="min-w-full divide-y divide-border text-sm">
                  <thead className="bg-muted/30 text-left text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3 font-medium">S/N</th>
                      <th className="px-4 py-3 font-medium">When</th>
                      <th className="px-4 py-3 font-medium">Gateway</th>
                      <th className="px-4 py-3 font-medium">Outcome</th>
                      <th className="px-4 py-3 font-medium">Order pay</th>
                      <th className="px-4 py-3 font-medium">Provider id</th>
                      <th className="px-4 py-3 font-medium">Order</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-card">
                    {items.map((row, rowIndex) => (
                      <Fragment key={row.id}>
                        <tr
                          className="cursor-pointer hover:bg-muted/20"
                          onClick={() => setExpandedId((prev) => (prev === row.id ? null : row.id))}
                        >
                          <td className="px-4 py-3 text-xs tabular-nums text-muted-foreground">
                            {(page - 1) * limit + rowIndex + 1}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-xs">{formatWhen(row.createdAt)}</td>
                          <td className="px-4 py-3 font-medium">{row.provider}</td>
                          <td className="max-w-[220px] truncate px-4 py-3 text-xs" title={row.outcome}>
                            {row.outcome}
                          </td>
                          <td className="px-4 py-3 text-xs">{row.orderPaymentMethod ?? '—'}</td>
                          <td
                            className="max-w-[140px] truncate px-4 py-3 font-mono text-xs"
                            title={row.providerResourceId ?? ''}
                          >
                            {row.providerResourceId ?? '—'}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs">{row.orderId ?? '—'}</td>
                        </tr>
                        {expandedId === row.id ? (
                          <tr className="bg-muted/10">
                            <td colSpan={7} className="space-y-3 px-4 py-4 text-foreground">
                              {row.detailMessage ? (
                                <div>
                                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                    Detail
                                  </p>
                                  <p className="text-sm">{row.detailMessage}</p>
                                </div>
                              ) : null}
                              {row.errorCode ? (
                                <p className="text-sm">
                                  <span className="font-semibold text-destructive">Error code:</span> {row.errorCode}
                                </p>
                              ) : null}
                              <div className="grid gap-2 text-xs sm:grid-cols-2">
                                <p>
                                  <span className="text-muted-foreground">Signature:</span>{' '}
                                  {row.signaturePresent ? 'present' : 'absent'}
                                </p>
                                <p>
                                  <span className="text-muted-foreground">Content-Type:</span>{' '}
                                  {row.contentType ?? '—'}
                                </p>
                                <p>
                                  <span className="text-muted-foreground">Body length:</span> {row.bodyLength ?? '—'}
                                </p>
                                <p>
                                  <span className="text-muted-foreground">Store id:</span> {row.storeId ?? '—'}
                                </p>
                              </div>
                              {row.bodyPreview ? (
                                <div>
                                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                    Body preview
                                  </p>
                                  <pre className="max-h-60 overflow-auto rounded-lg border border-border bg-background/80 p-3 text-xs whitespace-pre-wrap break-all">
                                    {row.bodyPreview}
                                  </pre>
                                </div>
                              ) : null}
                              {row.metadata && Object.keys(row.metadata).length > 0 ? (
                                <div>
                                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                    Metadata
                                  </p>
                                  <pre className="max-h-48 overflow-auto rounded-lg border border-border bg-background/80 p-3 text-xs">
                                    {JSON.stringify(row.metadata, null, 2)}
                                  </pre>
                                </div>
                              ) : null}
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    ))}
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                          No webhook rows match your filters.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>

              {total > 0 ? (
                <div className="mt-4 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-muted-foreground">
                    Page <span className="font-medium text-foreground">{page}</span> of{' '}
                    <span className="font-medium text-foreground">{totalPages}</span> — {total} total
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </>
      )}
    </Card>
  )
}
